/* =========================================================
   Cowork homepage interactions — ported from the standalone
   site's main.js and wrapped so it can be started on mount and
   fully torn down on unmount (SPA-safe: no leaked RAF/listeners).
   Returns a cleanup function.
   ========================================================= */
import { contactUs } from "../../services/forms";

export default function initHome() {
  const cleanups = [];
  let alive = true;

  const add = (target, type, fn, opts) => {
    target.addEventListener(type, fn, opts);
    cleanups.push(() => target.removeEventListener(type, fn, opts));
  };

  const prefersReduced = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  // enables the "hidden until revealed" CSS + dark viewport background
  document.documentElement.classList.add("js");
  document.body.classList.add("home");

  /* ---- Nav: frosted bar on scroll ---- */
  const nav = document.getElementById("nav");
  if (nav) {
    const onScroll = () => {
      nav.classList.toggle("is-scrolled", window.scrollY > 24);
    };
    onScroll();
    add(window, "scroll", onScroll, { passive: true });
  }

  /* ---- Mobile menu ---- */
  const navToggle = document.getElementById("nav-toggle");
  const mobileMenu = document.getElementById("mobile-menu");
  if (navToggle && mobileMenu) {
    const setMenu = (open) => {
      navToggle.setAttribute("aria-expanded", open ? "true" : "false");
      navToggle.setAttribute("aria-label", open ? "Close menu" : "Open menu");
      mobileMenu.classList.toggle("is-open", open);
      document.body.classList.toggle("menu-open", open);
    };
    add(navToggle, "click", () =>
      setMenu(!mobileMenu.classList.contains("is-open"))
    );
    mobileMenu.querySelectorAll("a").forEach((a) =>
      add(a, "click", () => setMenu(false))
    );
    add(document, "keydown", (e) => {
      if (e.key === "Escape" && mobileMenu.classList.contains("is-open")) {
        setMenu(false);
        navToggle.focus();
      }
    });
    add(window, "resize", () => {
      if (window.innerWidth > 860 && mobileMenu.classList.contains("is-open")) {
        setMenu(false);
      }
    });
  }

  /* ---- Scroll reveal ---- */
  const reveals = document.querySelectorAll(".reveal");
  if ("IntersectionObserver" in window && !prefersReduced) {
    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((e, i) => {
          if (e.isIntersecting) {
            e.target.style.transitionDelay = Math.min(i * 60, 240) + "ms";
            e.target.classList.add("is-visible");
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" }
    );
    reveals.forEach((el) => io.observe(el));
    cleanups.push(() => io.disconnect());
  } else {
    reveals.forEach((el) => el.classList.add("is-visible"));
  }

  /* ---- Cards: cursor-follow highlight ---- */
  document.querySelectorAll(".card, .case").forEach((card) => {
    add(card, "pointermove", (e) => {
      const r = card.getBoundingClientRect();
      card.style.setProperty("--mx", e.clientX - r.left + "px");
      card.style.setProperty("--my", e.clientY - r.top + "px");
    });
  });

  /* ---- Contact section: adapt to which CTA brought you here ----
     "Request relevant case studies" is a different ask from "Start a
     project", so the section rewords itself instead of showing the same
     generic form for both. */
  const CONTACT_MODES = {
    project: {
      eyebrow: "Start a project",
      title: "Let's talk about what you're building.",
      lead:
        "Tell us about your product and where you need engineering help. " +
        "You'll talk with an engineer who can actually dig into the problem — not a sales team.",
      expect: [
        "A technical conversation, not a pitch",
        "An honest view of how we'd approach it",
        "Clear next steps if it's a fit",
      ],
      label: "What are you building, and where do you need engineers?",
      submit: "Start the conversation",
    },
    cases: {
      eyebrow: "Case studies",
      title: "Which work would be most relevant?",
      lead:
        "Tell us what you're working on and we'll send case studies from " +
        "similar projects — with the engineering detail, not a brochure.",
      expect: [
        "Work close to what you're building",
        "The engineering detail, not marketing",
        "No sales follow-up unless you ask",
      ],
      label: "What are you working on? We'll match it to relevant work.",
      submit: "Request case studies",
    },
  };

  let contactMode = "project";
  const contactEls = {
    intro: document.getElementById("contact-intro"),
    eyebrow: document.getElementById("contact-eyebrow"),
    title: document.getElementById("contact-title"),
    lead: document.getElementById("contact-lead"),
    expect: document.getElementById("contact-expect"),
    label: document.getElementById("cf-message-label"),
    submit: document.getElementById("cf-submit"),
  };

  const setContactMode = (mode) => {
    const copy = CONTACT_MODES[mode];
    if (!copy || mode === contactMode || !contactEls.intro) return;
    contactMode = mode;
    // brief crossfade so the rewrite reads as intentional, not as a glitch
    contactEls.intro.style.transition = "opacity 0.16s ease";
    contactEls.intro.style.opacity = "0";
    setTimeout(() => {
      if (contactEls.eyebrow) contactEls.eyebrow.textContent = copy.eyebrow;
      if (contactEls.title) contactEls.title.textContent = copy.title;
      if (contactEls.lead) contactEls.lead.textContent = copy.lead;
      if (contactEls.expect) {
        contactEls.expect.innerHTML = copy.expect
          .map((t) => "<li>" + t + "</li>")
          .join("");
      }
      if (contactEls.label) contactEls.label.textContent = copy.label;
      if (contactEls.submit) contactEls.submit.textContent = copy.submit;
      contactEls.intro.style.opacity = "1";
    }, 160);
  };

  document.querySelectorAll('a[href="#contact"]').forEach((link) => {
    const mode = link.closest(".proof") ? "cases" : "project";
    add(link, "click", () => setContactMode(mode));
  });

  /* ---- Contact form → existing POST /contactUs API ----
     Backend requires companyName, email and projectDetails
     (phoneNumber is optional). Reuses the app's axios service so
     baseURL + credentials match the rest of the site. */
  const form = document.getElementById("contact-form");
  if (form) {
    const note = form.querySelector(".form__note");
    const btn = form.querySelector('button[type="submit"]');
    const TO = "contact@cowork.dev";

    const say = (msg, isError) => {
      if (!note) return;
      note.textContent = msg;
      note.classList.toggle("form__note--error", !!isError);
    };

    add(form, "submit", async (e) => {
      e.preventDefault();
      const hp = form.querySelector('[name="_gotcha"]');
      if (hp && hp.value) return;
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      const d = new FormData(form);
      const name = (d.get("name") || "").toString().trim();
      const email = (d.get("email") || "").toString().trim();
      const company = (d.get("company") || "").toString().trim();
      const message = (d.get("message") || "").toString().trim();

      const payload = {
        // The API appends its own fields to `body`, so this line leads the
        // email and tells us which CTA the enquiry came from.
        body:
          (contactMode === "cases"
            ? "Enquiry: Case studies request"
            : "Enquiry: New project") + "<br>",
        // companyName is mandatory server-side; fall back to the
        // person's name so Company can stay optional in the form.
        companyName: company || name,
        email: email,
        projectDetails: "Name: " + name + "\n\n" + message,
      };

      const original = btn ? btn.textContent : "";
      if (btn) { btn.disabled = true; btn.textContent = "Sending…"; }
      say("");
      try {
        const res = await contactUs(payload);
        if (res && res.status === 200) {
          form.reset();
          say("Thanks — we'll get back to you shortly.");
        } else {
          say("Something went wrong. Please email us at " + TO + ".", true);
        }
      } catch (err) {
        say("Couldn't send just now. Please email us at " + TO + ".", true);
      } finally {
        if (btn) { btn.disabled = false; btn.textContent = original; }
      }
    });
  }

  /* ---- Engineers: rotating dot globe ---- */
  (function initGlobe() {
    const cv = document.getElementById("globe");
    if (!cv) return;
    const g = cv.getContext("2d");
    let W, H, DPR, R, cx, cy, graf, t = 0;

    const N = 340;
    const pts = [];
    for (let i = 0; i < N; i++) {
      const y = 1 - (i / (N - 1)) * 2;
      const rad = Math.sqrt(Math.max(0, 1 - y * y));
      const theta = i * 2.399963229;
      pts.push({ x: Math.cos(theta) * rad, y: y, z: Math.sin(theta) * rad, eng: false });
    }
    const engIdx = [8, 47, 88, 129, 170, 205, 251, 296, 322];
    engIdx.forEach((i) => { if (pts[i]) pts[i].eng = true; });

    const TILT = -0.42;
    const st = Math.sin(TILT), ct = Math.cos(TILT);

    function slerp(a, b, s) {
      let d = a.x * b.x + a.y * b.y + a.z * b.z;
      d = Math.max(-1, Math.min(1, d));
      const om = Math.acos(d);
      if (om < 1e-4) return { x: a.x, y: a.y, z: a.z };
      const so = Math.sin(om);
      const w1 = Math.sin((1 - s) * om) / so, w2 = Math.sin(s * om) / so;
      return { x: a.x * w1 + b.x * w2, y: a.y * w1 + b.y * w2, z: a.z * w1 + b.z * w2 };
    }
    const SAMP = 26;
    const conns = [[8, 170], [47, 251], [88, 296], [129, 205], [170, 322], [8, 88]];
    const arcs = conns.map(function (c, k) {
      const a = pts[c[0]], b = pts[c[1]], seg = [];
      for (let s = 0; s <= SAMP; s++) {
        const u = s / SAMP;
        const p = slerp(a, b, u);
        const lift = 1 + 0.17 * Math.sin(Math.PI * u);
        seg.push({ x: p.x * lift, y: p.y * lift, z: p.z * lift });
      }
      return { seg: seg, phase: k * 0.37 };
    });

    function size() {
      DPR = Math.min(window.devicePixelRatio || 1, 2);
      const rect = cv.getBoundingClientRect();
      W = rect.width; H = rect.height || rect.width;
      cv.width = W * DPR; cv.height = H * DPR;
      g.setTransform(DPR, 0, 0, DPR, 0, 0);
      R = Math.min(W, H) * 0.44;
      cx = W / 2; cy = H / 2;
    }

    function draw() {
      g.clearRect(0, 0, W, H);
      const sinR = Math.sin(t), cosR = Math.cos(t);
      function tp(x, y, z) {
        const x1 = x * cosR + z * sinR;
        const z1 = -x * sinR + z * cosR;
        const y2 = y * ct - z1 * st;
        const z2 = y * st + z1 * ct;
        return { X: cx + x1 * R, Y: cy + y2 * R, Z: z2 };
      }
      const proj = [];
      for (const p of pts) { const q = tp(p.x, p.y, p.z); q.eng = p.eng; proj.push(q); }
      proj.sort((a, b) => a.Z - b.Z);
      for (const p of proj) {
        const depth = (p.Z + 1) / 2;
        if (p.eng) {
          const rr = 2.1 + depth * 2.3;
          g.beginPath();
          g.arc(p.X, p.Y, rr + 3.5, 0, 7);
          g.fillStyle = "rgba(255,106,26," + (0.10 + depth * 0.20) + ")";
          g.fill();
          g.beginPath();
          g.arc(p.X, p.Y, rr, 0, 7);
          g.fillStyle = "rgba(255,145,70," + (0.5 + depth * 0.5) + ")";
          g.fill();
        } else {
          g.beginPath();
          g.arc(p.X, p.Y, 0.7 + depth * 1.5, 0, 7);
          g.fillStyle = "rgba(208,210,225," + (0.08 + depth * 0.42) + ")";
          g.fill();
        }
      }
      for (const arc of arcs) {
        const pr = arc.seg.map((o) => tp(o.x, o.y, o.z));
        g.lineWidth = 1;
        for (let s = 0; s < pr.length - 1; s++) {
          const p0 = pr[s], p1 = pr[s + 1];
          const d = ((p0.Z + p1.Z) / 2 + 1) / 2;
          if (d < 0.4) continue;
          g.strokeStyle = "rgba(255,120,40," + (0.05 + d * 0.22) + ")";
          g.beginPath();
          g.moveTo(p0.X, p0.Y);
          g.lineTo(p1.X, p1.Y);
          g.stroke();
        }
        const sp = (t * 4 + arc.phase) % 1;
        for (let tr = 0; tr < 5; tr++) {
          const u = sp - tr * 0.022;
          if (u < 0) continue;
          const p = pr[Math.round(u * (pr.length - 1))];
          const d = (p.Z + 1) / 2;
          if (d < 0.42) continue;
          g.beginPath();
          g.arc(p.X, p.Y, 2.0 - tr * 0.28, 0, 7);
          g.fillStyle = "rgba(255,190,130," + ((0.6 - tr * 0.11) * (0.4 + d * 0.6)) + ")";
          g.fill();
        }
      }
    }

    function loop() {
      if (!alive) return;
      t += 0.0022;
      draw();
      graf = requestAnimationFrame(loop);
    }

    size();
    if (prefersReduced) draw();
    else loop();

    let gt;
    add(window, "resize", () => {
      clearTimeout(gt);
      gt = setTimeout(() => { if (alive) { size(); draw(); } }, 180);
    });
    add(document, "visibilitychange", () => {
      if (document.hidden) cancelAnimationFrame(graf);
      else if (alive && !prefersReduced) loop();
    });
    cleanups.push(() => cancelAnimationFrame(graf));
  })();

  /* ---- Hero: network constellation ---- */
  (function initHero() {
    const canvas = document.getElementById("network");
    if (!canvas || prefersReduced) return;

    const ctx = canvas.getContext("2d");
    let w, h, dpr, nodes, raf;
    const LINK_DIST = 150;

    function size() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.parentElement.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function seed() {
      const count = Math.min(Math.round((w * h) / 22000), 70);
      nodes = Array.from({ length: count }, (_, i) => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.18,
        vy: (Math.random() - 0.5) * 0.18,
        anchor: i === 0,
        r: i === 0 ? 3.4 : Math.random() * 1.4 + 0.9,
      }));
    }

    function frame() {
      if (!alive) return;
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d = Math.hypot(dx, dy);
          if (d < LINK_DIST) {
            const warm = a.anchor || b.anchor;
            const alpha = (1 - d / LINK_DIST) * (warm ? 0.5 : 0.16);
            ctx.strokeStyle = warm
              ? "rgba(255,106,26," + alpha + ")"
              : "rgba(255,255,255," + alpha + ")";
            ctx.lineWidth = warm ? 1 : 0.6;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > w) n.vx *= -1;
        if (n.y < 0 || n.y > h) n.vy *= -1;
        if (n.anchor) {
          const grad = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, 22);
          grad.addColorStop(0, "rgba(255,106,26,0.55)");
          grad.addColorStop(1, "rgba(255,106,26,0)");
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.arc(n.x, n.y, 22, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.fillStyle = n.anchor
          ? "rgba(255,140,60,0.95)"
          : "rgba(220,220,230,0.55)";
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(frame);
    }

    function start() {
      cancelAnimationFrame(raf);
      size();
      seed();
      frame();
    }

    start();

    let resizeTimer;
    add(window, "resize", () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => { if (alive) start(); }, 180);
    });
    add(document, "visibilitychange", () => {
      if (document.hidden) cancelAnimationFrame(raf);
      else if (alive) frame();
    });
    cleanups.push(() => cancelAnimationFrame(raf));
  })();

  /* ---- teardown ---- */
  return () => {
    alive = false;
    cleanups.forEach((fn) => fn());
    document.body.classList.remove("menu-open", "home");
  };
}
